/** Себестоимость в «Аналитике МП» (Ozon и WB, контракт mp_finance v1.4/v1.7): подписи схемы
 *  «Выручка − Расходы = Маржа», подсказки «—», плашка покрытия. Чистые функции, без React.
 *
 *  Себестоимость (`cogs_goods` — себестоимость производства с НДС, `cogs_wh` — склад производства без
 *  НДС) есть только с первой фиксации цен (`cogs.cogs_from`): у периода, начатого раньше, у площадки
 *  без себестоимости и без цен вовсе (`cogs.status = no_prices`, а не `cogs_from === null`) бэкенд
 *  отдаёт null себестоимости, «Расходов» и «Маржи» — показываем «—» с подсказкой почему, а не 0 и не
 *  удержания / маржу до себестоимости (решение владельца 22.10.2026). */
import type { CogsCoverage, CogsStatus } from "@/api/mpAnalytics";
import type { CogsView } from "@/api/ozonAnalytics";
import { fmtDate, fmtWhen, int, rub } from "./format";

/** Себестоимость на экранах — ОДНО число «Себестоимость» = себестоимость производства с НДС
 *  (`cogs_goods`) + склад производства (`cogs_wh`): продажи покупают товар по ней (решение владельца
 *  21.10.2026; до 22.10.2026 подпись — «Закупочная цена»); статьи витрины по отдельности не
 *  показываются. */
export const COGS_GROUP_LABEL = "Себестоимость";
export const COGS_HINT = "себестоимость производства с НДС + склад производства (без НДС)";
/** Схема экранов (решение владельца 22.10.2026): Выручка − Расходы = Маржа. «Расходы» — все
 *  удержания площадки + себестоимость (`expenses`), «Маржа» — `net_margin` (была «Чистая маржа»;
 *  маржа до себестоимости на экранах не показывается). */
export const EXPENSES_LABEL = "Расходы";
export const NET_MARGIN_LABEL = "Маржа";
/** Подсказка «Расходов»: из чего сложены. */
export function expensesHint(mpLabel: string): string {
  return `все удержания ${mpLabel} + себестоимость`;
}

/** Причина «—» и текст плашки, когда цен себестоимости ещё нет. */
export const NO_PRICES_TEXT = "цены себестоимости не зафиксированы";

/** «себестоимость считается с 01.09.2026» — почему нет маржи у периода (дня), начатого раньше. */
export function cogsFromText(cogsFrom: string | null): string {
  return `себестоимость считается с ${fmtDate(cogsFrom)}`;
}

/** Почему показателя себестоимости нет («—»): `which` — период (cur) или сравнение (cmp). */
export function cogsNullTitle(
  v: CogsView | null | undefined,
  mpLabel: string,
  which: "cur" | "cmp" = "cur",
): string {
  if (!v) return "себестоимость: нет данных";
  if (!v.supported) return `для ${mpLabel} нет себестоимости`;
  if (v.status === "no_prices") return NO_PRICES_TEXT;
  const what = which === "cmp" ? "период сравнения начат" : "период начат";
  return `${cogsFromText(v.cogs_from)} — ${what} раньше`;
}

/** Σ себестоимости (товар + склад) в знаке витрины; нет любой части — null. */
export function cogsTotal(
  goods: number | null | undefined,
  wh: number | null | undefined,
): number | null {
  if (goods === null || goods === undefined || wh === null || wh === undefined) return null;
  return Math.round((goods + wh) * 100) / 100;
}

/** «цены на 18.10.2026 07:00»; снимка нет — null. */
export function pricesAt(v: CogsView | null | undefined): string | null {
  return v?.prices_exported_at ? `цены на ${fmtWhen(v.prices_exported_at)}` : null;
}

/** Склонение: 1 товар, 2 товара, 5 товаров. */
export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const goods = (n: number): string => `${int(n)} ${plural(n, "товар", "товара", "товаров")}`;
const offers = (n: number): string => `${int(n)} ${plural(n, "оффер", "оффера", "офферов")}`;

/** Плашка покрытия рядом с плашкой сверки. */
export interface CoveragePlaque {
  /** warn — есть продажи без цены / без сопоставления; none — цен ещё нет; ok — покрыто всё;
   *  before — период целиком раньше начала себестоимости */
  kind: "warn" | "ok" | "none" | "before";
  /** части текста: «Нет себестоимости: …», «не сопоставлено с 1С: …» */
  parts: string[];
  /** «цены на …» */
  prices: string | null;
}

/** Статусы «нет сопоставления с 1С» — офферами (продукта 1С нет / он не однозначен / нет оффера). */
const UNMAPPED: { status: CogsStatus; label: string }[] = [
  { status: "unmapped", label: "не сопоставлено с 1С" },
  { status: "ambiguous", label: "неоднозначно в 1С" },
  { status: "no_offer", label: "без оффера (техкарточки)" },
];

export function coveragePlaque(
  c: Pick<CogsCoverage, "cogs" | "covered_from" | "date_from" | "statuses">,
): CoveragePlaque {
  const prices = pricesAt(c.cogs);
  if (c.cogs.status === "no_prices") {
    return { kind: "none", parts: [`Себестоимость: ${NO_PRICES_TEXT}`], prices };
  }
  if (c.covered_from === null || c.cogs.cogs_from === null) {
    return {
      kind: "before",
      parts: [`Себестоимость — с ${c.cogs.cogs_from ? fmtDate(c.cogs.cogs_from) : "—"}`],
      prices,
    };
  }
  const parts: string[] = [];
  const np = c.statuses.no_price;
  if (np.items > 0) {
    parts.push(`Нет себестоимости: ${goods(np.items)}, ${rub(np.revenue)} выручки`);
  }
  for (const u of UNMAPPED) {
    const s = c.statuses[u.status];
    if (s && s.offers > 0) parts.push(`${u.label}: ${offers(s.offers)}, ${rub(s.revenue)}`);
  }
  const partial = c.covered_from > c.date_from;
  if (parts.length === 0) {
    // период начат раньше себестоимости: «есть у всех продаж периода» было бы неправдой
    const text = partial
      ? `Себестоимость — с ${fmtDate(c.covered_from)}`
      : "Себестоимость есть у всех продаж периода";
    return { kind: "ok", parts: [text], prices };
  }
  if (partial) parts.push(`себестоимость — с ${fmtDate(c.covered_from)}`);
  return { kind: "warn", parts, prices };
}

/** Ссылка на раздел «Себестоимость», вкладка «Без цены», за тот же период и (если выбран) кабинет. */
export function missingPricesUrl(
  dateFrom: string,
  dateTo: string,
  legal?: string | null,
  mp?: string | null,
): string {
  const q = new URLSearchParams({ tab: "missing", from: dateFrom, to: dateTo });
  if (legal) q.set("legal", legal);
  if (mp) q.set("mp", mp);
  return `/purchase-prices?${q.toString()}`;
}

/** Ссылка на раздел «Себестоимость» (цены ещё не зафиксированы — «Без цены» пока нечего показать). */
export const PURCHASE_PRICES_URL = "/purchase-prices";
